export type SelectOptionPreset = {
  value: string;
  label: string;
};

export const SOCIAL_SECURITY_NUMBER_DATA_PRESET = {
  mask: "us_social_security",
  placeholder: "xxx-xx-xxxx",
} as const;

const FIXED_SELECT_OPTION_PRESETS: Record<string, SelectOptionPreset[]> = {
  gender: [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "trans", label: "Trans" },
    { value: "nonbinary", label: "Non-Binary" },
    { value: "other", label: "Other" },
  ],
  "citizenship_status": [
    { value: "", label: "Select one" },
    { value: "us_citizen", label: "US Citizen" },
    { value: "legal_permanent_resident", label: "Legal permanent resident" },
    { value: "work_auth", label: "Authorized to work in the US" },
    { value: "work_auth_pending", label: "Waiting on a work authorization VISA" },
    { value: "other", label: "Other" },
  ],
  "household_members_status": [
    { value: "", label: "Select one" },
    { value: "live_alone", label: "I will be living alone" },
    { value: "with_others", label: "Others will be living with me" },
  ],
};

const toObject = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
};

const toOption = (value: unknown): SelectOptionPreset => {
  const option = toObject(value);
  return {
    value: `${option.value || ""}`,
    label: `${option.label || ""}`,
  };
};

const sameOption = (left: SelectOptionPreset, right: SelectOptionPreset): boolean =>
  left.value === right.value && left.label === right.label;

export const getFixedSelectOptionPreset = (questionName: string): SelectOptionPreset[] | null => {
  const preset = FIXED_SELECT_OPTION_PRESETS[questionName];
  if (!preset) return null;

  return preset.map((option) => ({ ...option }));
};

export const isUsingFixedSelectOptionPreset = (
  questionName: string,
  optionsValue: unknown,
): boolean => {
  const preset = getFixedSelectOptionPreset(questionName);
  if (!preset) return false;
  if (!Array.isArray(optionsValue)) return false;

  const options = optionsValue.map(toOption);
  if (options.length !== preset.length) return false;
  return options.every((option, index) => sameOption(option, preset[index]));
};

export const applyQuestionDataPresets = (
  questionName: string,
  questionType: string,
  dataValue: Record<string, unknown>,
): Record<string, unknown> => {
  const data = { ...dataValue };

  if (questionType === "text" && questionName === "social_security_number") {
    data.mask = SOCIAL_SECURITY_NUMBER_DATA_PRESET.mask;
    data.placeholder = SOCIAL_SECURITY_NUMBER_DATA_PRESET.placeholder;
  }

  const fixedOptions = getFixedSelectOptionPreset(questionName);
  if (
    fixedOptions &&
    (questionType === "select" || questionType === "multiple")
  ) {
    data.options = fixedOptions;
  }

  return data;
};
